import { getFavoritesStore } from "./store.mjs";

export const config = () => ({
  ...window.weissenstadtFavorites,
  ...window.weissenstadtFavoritesList,
});
export const t = (key) =>
  window.weissenstadtFavorites?.strings?.[key] ||
  window.weissenstadtFavoritesList?.strings?.[key] ||
  key;

const DURATION = 7000;
const UNDO_DURATION = 12000;
// Inline SVGs from inc/icons.php (localized config); no second icon source.
const glyph = (name) => config().icons?.[name] || "";
const reducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function element(tag, className, attributes = {}) {
  const node = document.createElement(tag);
  node.className = className;
  for (const [name, value] of Object.entries(attributes))
    node.setAttribute(name, value);
  return node;
}

/** Without an explicit tone, the offered actions describe what happened. */
function toneFor(options) {
  if (options.error) return "error";
  if (options.tone) return options.tone;
  if (options.undo) return "removed";
  if (options.open) return "added";
  return "info";
}

function createFeedback() {
  const live = element("p", "screen-reader-text", {
    role: "status",
    "aria-live": "polite",
    "aria-atomic": "true",
  });
  live.dataset.feedbackLive = "";
  const box = element("aside", "favorites-feedback", {
    "aria-label": t("favorites"),
  });
  box.hidden = true;
  const icon = element("span", "favorites-feedback__icon", {
    "aria-hidden": "true",
  });
  const body = element("div", "favorites-feedback__body");
  const message = element("p", "favorites-feedback__message");
  message.dataset.feedbackMessage = "";
  const actions = element("div", "favorites-feedback__actions");
  const open = element("a", "favorites-feedback__action is-primary");
  open.href = config().listUrl;
  open.innerHTML = glyph("arrow");
  open.prepend(t("open"));
  const buttons = {};
  for (const action of ["undo", "reset"]) {
    const button = element("button", "favorites-feedback__action", {
      type: "button",
    });
    button.dataset.feedbackAction = action;
    button.textContent = t(action);
    if (action === "undo") button.insertAdjacentHTML("afterbegin", glyph("undo"));
    buttons[action] = button;
  }
  actions.append(open, buttons.undo, buttons.reset);
  body.append(message, actions);
  const close = element("button", "favorites-feedback__close", {
    type: "button",
    "aria-label": t("close"),
    title: t("close"),
  });
  close.dataset.feedbackAction = "close";
  close.innerHTML = glyph("close");
  const timerBar = element("span", "favorites-feedback__timer", {
    "aria-hidden": "true",
  });
  box.append(icon, body, close, timerBar);
  document.body.append(live, box);

  const onListPage = () => {
    try {
      return (
        new URL(config().listUrl, window.location.href).pathname ===
        window.location.pathname
      );
    } catch {
      return false;
    }
  };
  let undo;
  let returnFocus;
  let timeout;
  let remaining = null;
  let deadline;
  let progress;
  let leaving;
  let hovered = false;
  let announcementFrame;
  // A notice never expires unseen: hover, focus and background tabs pause it.
  const interacting = () =>
    hovered || document.hidden || box.contains(document.activeElement);

  // The timeout decides; the bar only mirrors it and pauses alongside it.
  function stopTimer() {
    clearTimeout(timeout);
    timeout = undefined;
    remaining = null;
    progress?.cancel();
    progress = null;
  }
  function startTimer(duration) {
    stopTimer();
    box.classList.toggle("has-timer", Boolean(duration));
    if (!duration) return;
    remaining = duration;
    progress = timerBar.animate(
      [{ transform: "scaleX(1)" }, { transform: "scaleX(0)" }],
      { duration, easing: "linear", fill: "forwards" },
    );
    progress.pause();
    box.classList.add("is-paused");
    resume();
  }
  function pause() {
    if (timeout === undefined) return;
    clearTimeout(timeout);
    timeout = undefined;
    remaining = Math.max(0, deadline - performance.now());
    progress?.pause();
    box.classList.add("is-paused");
  }
  function resume() {
    if (box.hidden || leaving || interacting()) return;
    if (remaining === null || timeout !== undefined) return;
    deadline = performance.now() + remaining;
    timeout = setTimeout(() => dismiss(), remaining);
    progress?.play();
    box.classList.remove("is-paused");
  }
  function show(wasHidden) {
    leaving?.cancel();
    leaving = null;
    box.classList.remove("is-leaving");
    box.hidden = false;
    if (reducedMotion()) {
      if (wasHidden)
        box.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160 });
      return;
    }
    if (wasHidden)
      box.animate(
        [
          { opacity: 0, transform: "translateY(1rem) scale(0.97)" },
          { opacity: 1, transform: "none" },
        ],
        { duration: 260, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
      );
    icon.animate(
      [
        { transform: "scale(0.5)" },
        { transform: "scale(1.15)", offset: 0.6 },
        { transform: "scale(1)" },
      ],
      { duration: 360, delay: wasHidden ? 80 : 0, easing: "ease-out" },
    );
  }
  function dismiss(restoreFocus = false) {
    stopTimer();
    undo = null;
    hovered = false;
    cancelAnimationFrame(announcementFrame);
    live.textContent = "";
    if (restoreFocus) {
      const target = returnFocus?.isConnected
        ? returnFocus
        : document.querySelector(".favorite-title, [data-favorites-nav] a");
      target?.focus({ preventScroll: true });
    }
    if (box.hidden || leaving) return;
    if (document.hidden) {
      box.hidden = true;
      return;
    }
    box.classList.add("is-leaving");
    const exit = box.animate(
      [
        { opacity: 1 },
        {
          opacity: 0,
          transform: reducedMotion() ? "none" : "translateY(0.5rem)",
        },
      ],
      { duration: 180, easing: "ease-in" },
    );
    leaving = exit;
    exit.onfinish = () => {
      // A queued finish must not hide a notice shown again in the meantime.
      if (leaving !== exit) return;
      leaving = null;
      box.classList.remove("is-leaving");
      box.hidden = true;
    };
  }
  function notify(text, options = {}) {
    if (!box.contains(document.activeElement))
      returnFocus = document.activeElement;
    const focused = document.activeElement;
    const wasHidden = box.hidden || Boolean(leaving);
    undo = options.undo;
    const canUndo = Boolean(undo?.removed || undo?.removedItems?.length);
    const tone = toneFor(options);
    if (box.dataset.tone !== tone) icon.innerHTML = glyph(tone);
    box.dataset.tone = tone;
    message.textContent = text;
    open.hidden = !options.open || onListPage();
    buttons.undo.hidden = !canUndo;
    buttons.reset.hidden = options.error !== "corrupt";
    actions.hidden = open.hidden && buttons.undo.hidden && buttons.reset.hidden;
    show(wasHidden);
    if (box.contains(focused) && focused.hidden)
      close.focus({ preventScroll: true });
    // Keep the live region present before announcing, including repeated text.
    cancelAnimationFrame(announcementFrame);
    live.textContent = "";
    announcementFrame = requestAnimationFrame(() => {
      live.textContent = text;
    });
    startTimer(options.error ? 0 : canUndo ? UNDO_DURATION : DURATION);
  }
  box.addEventListener("pointerenter", (event) => {
    if (event.pointerType === "touch") return;
    hovered = true;
    pause();
  });
  box.addEventListener("pointerleave", () => {
    hovered = false;
    resume();
  });
  box.addEventListener("focusin", pause);
  box.addEventListener("focusout", () => queueMicrotask(resume));
  document.addEventListener("visibilitychange", () =>
    document.hidden ? pause() : resume(),
  );
  box.addEventListener("click", (event) => {
    const action = event.target.closest("[data-feedback-action]")?.dataset
      .feedbackAction;
    if (action === "undo" && undo) {
      const bulk = Boolean(undo.removedItems);
      const store = getFavoritesStore();
      const result = bulk
        ? store.restoreMany(undo.removedItems)
        : store.restore(undo.removed, undo.before);
      if (!result.ok)
        return notify(t(result.error), { error: result.error, undo });
      notify(t(bulk ? "listRestored" : "restored"), { tone: "added" });
    } else if (action === "reset") {
      const result = getFavoritesStore().reset();
      if (!result.ok) return showError(result.error);
      notify(t("resetDone"));
    } else if (action === "close") dismiss(true);
  });
  document.addEventListener("keydown", (event) => {
    // Panels that handle Escape themselves (e.g. sharing) keep the notice open.
    if (event.key === "Escape" && !event.defaultPrevented && !box.hidden)
      dismiss(box.contains(document.activeElement));
  });
  return { notify };
}

function getFeedback() {
  window.weissenstadtFavoritesFeedback ||= createFeedback();
  return window.weissenstadtFavoritesFeedback;
}

export function notify(message, options = {}) {
  getFeedback().notify(message, options);
}

export function showError(error) {
  notify(t(error), { error });
}

if (document.readyState === "loading")
  document.addEventListener("DOMContentLoaded", getFeedback, { once: true });
else getFeedback();
